/**
 * Platform-agnostic Firebase initialization for Travelix.
 *
 * The web app calls initTravelixFirebase() with browserLocalPersistence (default);
 * a future native runtime could pass a different Auth persistence adapter.
 *
 * Scope: Auth + Firestore only. Travelix v2 has no file uploads — see
 * docs/decisions.md D-015 for the rationale. If a future phase adds user
 * uploads, wire firebase/storage back through here.
 */

import { initializeApp, getApp, getApps, type FirebaseApp } from "firebase/app";
import {
  initializeAuth,
  getAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  type Auth,
  type Persistence,
} from "firebase/auth";
import { getFirestore, initializeFirestore, type Firestore } from "firebase/firestore";
import { getFunctions, type Functions } from "firebase/functions";

export interface TravelixFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  /** Present in the standard Firebase web config even when Storage isn't wired. */
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface TravelixFirebaseHandles {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
}

let handles: TravelixFirebaseHandles | null = null;

/**
 * Initialize Firebase once for the whole app.
 *
 * @param config  Firebase web config (from env vars).
 * @param persistence  Auth persistence — defaults to browserLocalPersistence for web/PWA.
 */
export function initTravelixFirebase(
  config: TravelixFirebaseConfig,
  persistence: Persistence | Persistence[] = browserLocalPersistence,
): TravelixFirebaseHandles {
  if (handles) return handles;

  const app = getApps().length === 0 ? initializeApp(config) : getApp();

  let auth: Auth;
  try {
    // popupRedirectResolver is required for signInWithPopup / signInWithRedirect
    // to work when using the tree-shakable initializeAuth entry point. Without
    // it the web Google SSO button throws auth/argument-error at call time
    // (native still works because that path uses signInWithCredential with an
    // idToken from the Capacitor Firebase plugin).
    auth = initializeAuth(app, {
      persistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    // initializeAuth throws if called twice (hot reload); fall back to getAuth
    auth = getAuth(app);
  }

  // Firestore rejects field values that are literal `undefined`, which
  // breaks any write whose payload has optional keys set to undefined
  // (e.g. an expense with no FX conversion, or a same-user payer). Turn
  // on ignoreUndefinedProperties so the SDK silently drops those keys
  // instead — matches the server behavior and removes the need for
  // per-write pruning at every call site. `initializeFirestore` must run
  // before any `getFirestore(app)` call for the same app, so we guard
  // against a hot-reload double-init.
  let db: Firestore;
  try {
    db = initializeFirestore(app, { ignoreUndefinedProperties: true });
  } catch {
    db = getFirestore(app);
  }
  handles = { app, auth, db };
  return handles;
}

function assertReady(): TravelixFirebaseHandles {
  if (!handles) {
    throw new Error(
      "[travelix/core] Firebase not initialized. Call initTravelixFirebase() at app boot.",
    );
  }
  return handles;
}

export function getFirebaseApp(): FirebaseApp {
  return assertReady().app;
}

export function getFirebaseAuth(): Auth {
  return assertReady().auth;
}

export function getFirestoreDb(): Firestore {
  return assertReady().db;
}

/**
 * Lazily construct a Functions client. We don't cache one on `handles` because
 * callers may pass a non-default region in the future; recomputing per call is
 * essentially free (the SDK caches internally).
 */
export function getFirebaseFunctions(region?: string): Functions {
  return region
    ? getFunctions(getFirebaseApp(), region)
    : getFunctions(getFirebaseApp());
}
